import { IOneTimeInvoiceFormSchemaWithOfferings } from '@monetize/types/app';
import {
  formatCurrency,
  formatInteger,
  getCurrencyDecimals,
  sortByProductPosition,
} from '@monetize/utils/core';
import { formatISO } from 'date-fns/formatISO';
import { Fragment, useCallback } from 'react';
import { useFieldArray, useFormContext, useFormState } from 'react-hook-form';
import { handleApiErrorToast } from '../../../../api/axios';
import { doGetOfferingById } from '../../../../api/productCatalogService';
import { HeaderCell } from '../../../../components/Invoices';
import {
  MBox,
  MDivider,
  MFormField,
  MSimpleGrid,
  MText,
} from '../../../../components/Monetize';
import { QuoteBodyAccordion } from '../../../../components/Quotes/QuoteBodyAccordion';
import {
  GRID_MIN_WIDTH,
  GRID_PROPS,
  HEADER_ROW_CELLS,
} from '../../../../constants/oneTimeInvoice';
import { logger } from '../../../../services/logger';
import { roundNumberToDecimal } from '../../../../utils/numberUtils';
import {
  getOneTimeInvoiceLineAmount,
  getProductObj,
  isOneTimeInvoiceProductSelected,
} from '../oneTimeInvoice.utils';
import { useOneTimeInvoice } from './oneTimeInvoiceContext';
import { OneTimeInvoiceOffering } from './OneTimeInvoiceOffering';
import { OneTimeInvoiceOfferingDropdown } from './OneTimeInvoiceOfferingDropdown';
import { TotalRow } from './TotalRow';

const TOTAL_ROW_CONTAINER_PROPS = {
  background: 'tGray.support',
  borderRadius: '7px',
};
const TOTAL_ROW_TEXT_PROPS = { fontSize: 'lg' };

export const OneTimeInvoiceProducts = ({ value }: { value?: string }) => {
  const { currency } = useOneTimeInvoice();
  const { control, getValues, setValue, watch } =
    useFormContext<IOneTimeInvoiceFormSchemaWithOfferings>();
  const { errors } = useFormState({ control });

  const {
    fields: offeringFields,
    remove,
    update,
  } = useFieldArray({ control, name: 'offerings' });

  const watchedOfferings = watch('offerings') || [];

  const handleAddOffering = useCallback(
    async (offeringId: string) => {
      try {
        const offering = await doGetOfferingById(offeringId);
        const sortedOfferingProducts = sortByProductPosition(
          offering.offeringProducts,
        );

        if (offering.products.length <= 0) {
          handleApiErrorToast(
            new Error('The selected Offering does not have any products.'),
          );
          return;
        }
        setValue('offerings', [
          ...(getValues().offerings || []),
          {
            offeringId: offering.id,
            offeringDescription: offering.name,
            startDate: formatISO(new Date(), { representation: 'date' }),
            endDate: null,
            products: sortedOfferingProducts.map((offeringProduct) =>
              getProductObj({
                ...offeringProduct.product,
                isMandatory: offeringProduct.isMandatory,
                isSelected: offeringProduct.isMandatory,
                position: offeringProduct.position,
                isPriceTierLockEligible:
                  offeringProduct.isPriceTierLockEligible,
              }),
            ),
            metadata: { loading: false, offering },
          },
        ]);
      } catch (ex) {
        logger.error('Error while adding offering', ex);
        handleApiErrorToast(
          new Error(
            'There was an error updating your offering, please try again.',
          ),
        );
      }
    },
    [getValues, setValue],
  );

  const handleUpdateOffering = useCallback(
    async (index: number, offeringId: string) => {
      try {
        const formOffering = getValues(`offerings.${index}`);
        update(index, {
          ...formOffering,
          metadata: { ...formOffering.metadata, loading: true },
        });
        const offering = await doGetOfferingById(offeringId);
        const sortedOfferingProducts = sortByProductPosition(
          offering.offeringProducts,
        );
        // Revert back if offering has no products
        if (offering.products.length <= 0) {
          update(index, formOffering);
          handleApiErrorToast(
            new Error('The selected Offering does not have any products.'),
          );
          return;
        }
        update(index, {
          ...formOffering,
          offeringId: offering.id,
          offeringDescription: offering.name,
          products: sortedOfferingProducts.map((offeringProduct) =>
            getProductObj({
              ...offeringProduct.product,
              isMandatory: offeringProduct.isMandatory,
              isSelected: offeringProduct.isMandatory,
              position: offeringProduct.position,
              isPriceTierLockEligible: offeringProduct.isPriceTierLockEligible,
            }),
          ),
          metadata: { loading: false, offering },
        });
      } catch (ex) {
        logger.error('Error while updating offering', ex);
        handleApiErrorToast(
          new Error(
            'There was an error updating your offering, please try again.',
          ),
        );
      }
    },
    [getValues, update],
  );

  const total = watchedOfferings
    .flatMap(({ products }) =>
      products
        .filter(isOneTimeInvoiceProductSelected)
        .map(({ quantity, unitPrice }) =>
          getOneTimeInvoiceLineAmount(quantity, unitPrice, currency),
        ),
    )
    .reduce((acc, curr) => (curr += acc), 0);

  // lines are already rounded; this only clears float noise from the sum
  const formattedTotal = formatCurrency(
    roundNumberToDecimal(total, getCurrencyDecimals(currency)),
    { currency },
  );

  return (
    <QuoteBodyAccordion
      value={value}
      title={`Products and Pricing (${formatInteger(
        offeringFields.length,
      )} included)`}
    >
      <MBox overflowX="auto" p="1" m="-1">
        {/* Pin every row to the table width so the header, offering rows and
            totals stay aligned once the section scrolls horizontally. */}
        <MBox minW={GRID_MIN_WIDTH}>
          <MSimpleGrid {...GRID_PROPS}>
            {HEADER_ROW_CELLS.map(({ label, value, isRequired }) => (
              <HeaderCell
                key={value}
                value={value}
                isRequired={isRequired}
                pr={label === 'Amount' ? 4 : undefined}
              >
                {label}
              </HeaderCell>
            ))}
          </MSimpleGrid>
          <MDivider gridColumn="1/-1" />

          {offeringFields.map((item, index) => (
            <Fragment key={item.id}>
              <OneTimeInvoiceOffering
                control={control}
                getValues={getValues}
                errors={errors}
                index={index}
                values={item}
                handleRemove={remove}
                handleUpdate={handleUpdateOffering}
                currency={currency}
              />
              <MDivider />
            </Fragment>
          ))}

          <MFormField py="2" maxW="12.5rem">
            <OneTimeInvoiceOfferingDropdown onChange={handleAddOffering} />
          </MFormField>
          <MDivider gridColumn="1/-1" />

          {/* Laid out on the table's own grid so the totals stay flush with the
              Amount column at every width. */}
          <MSimpleGrid {...GRID_PROPS} alignItems="stretch">
            <MBox gridColumn="1/4" />
            <MBox gridColumn="4/-1">
              <TotalRow leftColumn="SubTotal" rightColumn={formattedTotal} />
              <MDivider />
              <TotalRow
                leftColumn="Tax"
                rightColumn={
                  <MText
                    fontStyle="italic"
                    fontSize="md"
                    textAlign="right"
                    fontWeight="400"
                    color="tGray.darkPurple"
                    pr="2"
                  >
                    Calculated on Review
                  </MText>
                }
              />
              <MDivider />
              <TotalRow
                leftColumn="Total"
                containerProps={TOTAL_ROW_CONTAINER_PROPS}
                textProps={TOTAL_ROW_TEXT_PROPS}
                rightColumn={formattedTotal}
              />
            </MBox>
          </MSimpleGrid>
        </MBox>
      </MBox>
    </QuoteBodyAccordion>
  );
};
